import React, { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageCircle, Send, ExternalLink, Copy } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";

export default function Communications() {
  const [message, setMessage] = useState("Olá {nome}! Aviso da academia:\n\n");
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!message.trim()) return toast.error("Escreva uma mensagem");
    setLoading(true);
    try {
      // Send raw template; each recipient replaces {nome} client-side for personalization
      const { data } = await api.post("/broadcast/whatsapp", { message });
      const personalized = data.recipients.map((r) => {
        const msg = message.replace(/\{nome\}/gi, r.name.split(" ")[0]);
        const encoded = encodeURIComponent(msg);
        return { ...r, wa_link: `https://wa.me/${r.phone}?text=${encoded}` };
      });
      setRecipients(personalized);
      toast.success(`${personalized.length} contatos preparados`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  };

  const openAll = () => {
    if (recipients.length === 0) return;
    if (!window.confirm(`Abrir ${recipients.length} janelas do WhatsApp?`)) return;
    recipients.forEach((r, i) => setTimeout(() => window.open(r.wa_link, "_blank"), i * 400));
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Comunicados WhatsApp"
        subtitle="Envie avisos em massa para os alunos"
      />

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 border border-slate-200">
          <label className="text-sm font-semibold text-slate-700 mb-2 block">Mensagem</label>
          <Textarea rows={8} value={message} onChange={(e) => setMessage(e.target.value)}
            data-testid="broadcast-message-input"
            placeholder="Ex: Aviso: aula extra sábado às 9h!"/>
          <p className="text-xs text-slate-500 mt-2">Use <code className="bg-slate-100 px-1 rounded">{"{nome}"}</code> para personalizar com o primeiro nome do aluno.</p>
          <Button onClick={generate} disabled={loading}
            data-testid="broadcast-generate-button"
            className="mt-4 w-full bg-rose-600 hover:bg-rose-700">
            <MessageCircle className="w-4 h-4 mr-2"/>{loading ? "Gerando..." : "Gerar links WhatsApp"}
          </Button>
        </Card>

        <Card className="p-5 border border-slate-200 bg-emerald-50/30">
          <h3 className="font-heading font-bold text-slate-900 mb-3 flex items-center gap-2">
            <Send className="w-4 h-4 text-emerald-600"/>{recipients.length} destinatários prontos
          </h3>
          {recipients.length === 0 ? (
            <p className="text-sm text-slate-500">Gere a lista para enviar aos alunos com telefone cadastrado.</p>
          ) : (
            <>
              <Button onClick={openAll} data-testid="broadcast-open-all-button"
                className="w-full bg-emerald-600 hover:bg-emerald-700 mb-3">
                <ExternalLink className="w-4 h-4 mr-2"/>Abrir todos no WhatsApp
              </Button>
              <div className="max-h-96 overflow-y-auto space-y-1" data-testid="broadcast-list">
                {recipients.map((r) => (
                  <div key={r.student_id} className="flex items-center justify-between p-2 bg-white rounded-lg border border-slate-200">
                    <div>
                      <div className="text-sm font-medium text-slate-900">{r.name}</div>
                      <div className="text-xs text-slate-500 font-mono">+{r.phone}</div>
                    </div>
                    <a href={r.wa_link} target="_blank" rel="noreferrer"
                      className="text-xs bg-emerald-600 text-white px-3 py-1 rounded-full font-semibold hover:bg-emerald-700">
                      Enviar →
                    </a>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
